import { useCallback, useEffect, useRef, useState } from "react";
import { api, act, post } from "../lib/api";
import { clock, dur, hrs, hmin, hm12, istHM, istHM24, useLive, useTick, syncClock, dayLabel, hue } from "../lib/time";
import { hueOf } from "../lib/api";

const FIRST = new Set(["first"]);
const MYDAY_EVENTS = (t) => /^(task|timer|segment|break|person|leave|calendar|day)\./.test(t) || t === "autostop";
// /me carries today, caps and the shift: refetch it only when one of those can have changed
const ME_EVENTS = /^(resync|day\.rollover|person\.change|calendar\.change|leave\.)/;
const istMinutes = (hm) => { const [h, m] = hm.split(":").map(Number); return h * 60 + m; };
import { ClientChip, StateTag, Chips, Kbd } from "../components/bits";
import { UnplannedSheet, AddTimeSheet } from "../components/Sheets";
import { EstLabel } from "../components/EstimatePicker";

const today = (c, t) => (c.todayClosedMs || 0) + t - c.runningSince;

const PAUSE = [["feedback", "Feedback came in"], ["waiting_client", "Waiting on client"], ["waiting_internal", "Waiting on team"],
  ["meeting", "Meeting"], ["break", "Break"], ["switch", "Skip"]];
const PARKED = ["waiting_client", "waiting_internal", "on_hold", "not_given"];

function NowPlaying({ card, names, brk, next, onPause, onDone, onEndBreak }) {
  const t = useTick();
  const [mode, setMode] = useState(null);
  useEffect(() => { const f = () => setMode("done"); window.addEventListener("ck-key-complete", f); return () => window.removeEventListener("ck-key-complete", f); }, []);
  if (brk) {
    return (
      <section className="np np-break" data-testid="now-playing">
        <div className="lbl lbl-w">On break</div>
        <div className="np-timer" data-testid="break-timer">{clock(t - brk.start)}</div>
        <p className="np-sub">Since {istHM(brk.start)}</p>
        <button className="btn-mari" onClick={onEndBreak} data-testid="end-break-button">End break</button>
      </section>
    );
  }
  if (!card) {
    return (
      <section className="np np-idle" data-testid="now-playing">
        <div className="lbl lbl-w">Now playing</div>
        <h2 className="np-title">Nothing on the clock</h2>
        <p className="np-sub">Play a card from your list to start its timer.</p>
      </section>
    );
  }
  return (
    <section className="np" data-testid="now-playing">
      <div className="np-tag">■ Now playing</div>
      <ClientChip code={card.client} name={names[card.client]} label />
      <h2 className="np-title" data-testid="now-playing-title">{card.title}</h2>
      <div className="np-timer" data-testid="now-playing-timer">{clock(today(card, t))}</div>
      <p className="np-sub">Since {istHM(card.runningSince)} / estimate {card.estimateLabel}</p>
      {next && <div className="np-next"><span className="lbl lbl-w">Up next</span><ClientChip code={next.client} name={names[next.client]} /><span>{next.title}</span><b className="num ml-auto">{next.estimateLabel}</b></div>}
      {mode === null && (
        <div className="np-actions">
          <button className="btn-mari" onClick={() => setMode("pause")} data-testid="pause-button">Pause<Kbd k="P" /></button>
          <button className="btn-line-w" onClick={() => setMode("done")} data-testid="complete-button">Complete<Kbd k="C" dark /></button>
        </div>
      )}
      {mode === "pause" && (
        <div className="np-pick"><p className="np-sub">Paused. Why? (optional)</p>
          <Chips items={PAUSE} testid="pause-reason" onPick={(r) => { setMode(null); onPause(card, r); }} /></div>
      )}
      {mode === "done" && (
        <div className="np-pick"><p className="np-sub">Done?</p>
          <div className="done-opts">
            <button className="opt" onClick={() => { setMode(null); onDone(card, "done"); }} data-testid="complete-done">
              <b>Done</b><span>Off your list, onto the done pile.</span></button>
            <button className="opt" onClick={() => { setMode(null); onDone(card, "feedback"); }} data-testid="complete-feedback">
              <b>Sent for feedback</b><span>Parks it with the client. Changes come back as a Revision card.</span></button>
          </div>
        </div>
      )}
    </section>
  );
}

function CardRow({ t, n, names, onPlay, onAddTime, off }) {
  const now = useTick(30000);
  const logged = t.loggedMs + (t.runningSince ? now - t.runningSince : 0);
  const est = t.estimate?.hours ? t.estimate.hours * 3600000 : null;
  const parked = PARKED.includes(t.state) || t.kind === "standby";
  let label = "Play", body = { taskId: t.id }, path = "/timer/play";
  if (t.state === "waiting_client") { label = "Changes in"; path = "/timer/feedback-in"; }
  else if (parked) { label = "Resume"; body = { ...body, force: true }; }
  return (
    <li className={`crow ${t.runningSince ? "live" : ""} ${parked ? "parked" : ""}`} data-testid={`myday-card-${t.id}`}>
      {n != null && <span className="crow-n num">{String(n).padStart(2, "0")}</span>}
      <div className="crow-main">
        <div className="crow-top"><ClientChip code={t.client} name={names[t.client]} /><b className="crow-title">{t.title}</b><StateTag t={t} /></div>
        <div className="crow-meta"><b>{names[t.client] || t.client}</b>{t.kind === "revision" ? <span className="rev-t"> / Revision</span> : t.kind === "adapt" ? " / Adapt" : " / New"}
          {t.moreTime?.status === "added" && <span className="more"> / +{hrs(t.moreTime.hours)} added</span>}
          {est && logged > est && <button className="link" onClick={() => onAddTime(t)} data-testid={`addtime-open-${t.id}`}>Over the estimate. Add time?</button>}
        </div>
      </div>
      <div className="crow-est num">{EstLabel({ t }) || <b>{t.estimateLabel}</b>}<span>{dur(logged)}</span></div>
      {!off && t.state !== "done" && !t.runningSince && (
        <button className={`btn-sq ${label === "Play" ? "" : "alt"}`} aria-label={`${label}: ${t.title}`} onClick={() => onPlay(path, body)} data-testid={`play-${t.id}`}>
          {label === "Play" ? "▶" : <small>{label}</small>}</button>)}
      {t.runningSince && <span className="btn-sq live" aria-hidden="true">❚❚</span>}
    </li>
  );
}

function Ribbon({ tasks, segs, me }) {
  const now = useTick(30000);
  const a = Date.parse(`${me.today}T${me.shift.start}:00+05:30`), b = Date.parse(`${me.today}T${me.shift.end}:00+05:30`);
  const pos = (t) => `${Math.max(0, Math.min(100, ((t - a) / (b - a)) * 100))}%`;
  const cap = me.shift.capacityHours;
  let acc = 0;
  const plan = tasks.filter((t) => !["not_given", "on_hold"].includes(t.state)).map((t) => {
    const h = t.kind === "standby" ? 0 : t.estimate?.hours || 0;
    const from = acc; acc += h;
    return { t, from, h };
  }).filter((x) => x.h > 0);
  const total = Math.max(cap, acc);
  const mins = (x) => Number(x.slice(0, 2)) * 60 + Number(x.slice(3, 5));
  const sm = mins(me.shift.start), em = mins(me.shift.end);
  const sh = Math.ceil((sm + 45) / 60), eh = Math.floor((em - 45) / 60);
  const col = (c) => `oklch(0.62 0.15 ${hueOf(c) ?? hue(c)})`;
  return (
    <div className="ribbon" data-testid="myday-ribbon" aria-label="Plan against the clock">
      <span className="lbl">Plan</span>
      <div className="rb-line rb-plan"><i className="rb-cap" style={{ left: `${(cap / total) * 100}%` }} title={`${cap} h`} />
        {plan.map(({ t, from, h }) => <i key={t.id} className={`rb-blk ${t.state === "done" ? "done" : ""}`} title={`${t.title}, ${hrs(h)}`} style={{ left: `${(from / total) * 100}%`, width: `${(h / total) * 100}%`, background: col(t.client) }} />)}</div>
      <span className="lbl">Clock</span>
      <div className="rb-line rb-clock">
        {segs.map((g) => <i key={g.id} className={`rb-blk ${g.end ? "" : "live"}`} title={`${g.title} ${istHM(g.start)} to ${g.end ? istHM(g.end) : "now"}`} style={{ left: pos(g.start), width: `calc(${pos(g.end || now)} - ${pos(g.start)})`, background: col(g.client) }} />)}
        <i className="rb-now" style={{ left: pos(now) }} aria-label="Now" /></div>
      <span />
      <div className="rb-ticks"><span className="num" style={{ left: 0 }}>{hm12(me.shift.start)}</span>
        {Array.from({ length: Math.max(0, eh - sh + 1) }, (_, i) => sh + i).map((h) => <span key={h} className="num mid" style={{ left: pos(Date.parse(`${me.today}T${String(h).padStart(2, "0")}:00:00+05:30`)) }}>{`${h % 12 || 12} ${h < 12 ? "am" : "pm"}`}</span>)}
        <span className="num" style={{ right: 0 }}>{hm12(me.shift.end)}</span></div>
    </div>
  );
}

function Stats({ tasks, row, shift }) {
  const now = useTick(30000);
  const planned = tasks.filter((t) => t.kind !== "standby").reduce((s, t) => s + (t.estimate?.hours || 0), 0);
  const logged = tasks.reduce((s, t) => s + t.loggedMs + (t.runningSince ? now - t.runningSince : 0), 0);
  const noEst = tasks.filter((t) => t.state !== "done" && !t.estimate?.hours && t.estimate?.kind !== "fullday").length;
  return (
    <div className="mstats" data-testid="myday-stats">
      <div><span className="lbl">Planned</span><b className="num">{hrs(planned)}<small> of {hrs(shift.capacityHours)}</small></b></div>
      <div><span className="lbl">Logged</span><b className="num" data-testid="myday-logged">{dur(logged)}</b></div>
      <div><span className="lbl">No estimate</span><b className="num">{noEst}<small> cards</small></b></div>
      <div><span className="lbl">Break left</span><b className="num">{dur(row?.breakLeftMs ?? shift.breakMinutes * 60000)}<small> of {hmin(shift.breakMinutes)}</small></b></div>
    </div>
  );
}

function Hours({ tasks, names, segs }) {
  const by = {};
  tasks.forEach((t) => { if (t.loggedMs > 0) by[t.client] = (by[t.client] || 0) + t.loggedMs; });
  const list = Object.entries(by).sort((a, b) => b[1] - a[1]);
  const max = Math.max(1, ...list.map(([, v]) => v));
  return (
    <section className="mhours" data-testid="myday-hours">
      <div className="lbl">Where your hours went</div>
      {!list.length && <p className="hint0">Nothing logged yet today.</p>}
      {list.map(([c, v]) => <div key={c} className="mh-row"><ClientChip code={c} name={names[c]} /><span className="mh-bar"><i style={{ width: `${(v / max) * 100}%` }} /></span><b className="num">{dur(v)}</b></div>)}
      {segs.length > 0 && <><div className="lbl mt-s">Every stretch, newest first</div>
        <ul className="mh-segs">{segs.map((g) => (
          <li key={g.id}><span className="num">{istHM(g.start)} to {g.end ? istHM(g.end) : "now"}</span><ClientChip code={g.client} name={names[g.client]} /><span className="trunc">{g.title}</span><b className="num">{dur((g.end || Date.now()) - g.start)}</b></li>))}</ul></>}
      <p className="hint0">Time looks wrong? Tell the manager who planned the card. Every fix is logged.</p>
    </section>
  );
}

function Dock({ card, names, brk, onPause, onDone, onBreak, onEndBreak }) {
  const t = useTick();
  const [ask, setAsk] = useState(false);
  if (brk) return (
    <div className="dock" data-testid="phone-dock"><span className="lbl lbl-w">Break</span><b className="dock-t">{clock(t - brk.start)}</b>
      <button className="btn-mari sm" onClick={onEndBreak} data-testid="dock-back">Back</button></div>
  );
  if (!card) return null;
  return (
    <div className="dock" data-testid="phone-dock">
      <ClientChip code={card.client} name={names[card.client]} label /><b className="dock-t" data-testid="dock-timer">{clock(today(card, t))}</b>
      {ask ? <>
        <button className="btn-mari sm" onClick={() => { setAsk(false); onDone(card, "done"); }} data-testid="dock-done">Done</button>
        <button className="btn-line-w sm" onClick={() => { setAsk(false); onDone(card, "feedback"); }} data-testid="dock-feedback">Sent for feedback</button>
        <button className="btn-line-w sm" aria-label="Cancel" onClick={() => setAsk(false)} data-testid="dock-cancel">×</button>
      </> : <>
        <button className="btn-mari sm" aria-label="Pause" onClick={() => onPause(card, "switch")} data-testid="dock-pause">❚❚</button>
        <button className="btn-line-w sm" onClick={() => setAsk(true)} data-testid="dock-done-ask">Done?</button>
        <button className="btn-line-w sm" aria-label="Break" onClick={onBreak} data-testid="dock-break">Break</button>
      </>}
    </div>
  );
}

export default function MyDay({ me, refreshMe }) {
  const [tasks, setTasks] = useState(null);
  const [names, setNames] = useState({});
  const [clients, setClients] = useState([]);
  const [sheet, setSheet] = useState(null);
  const uid = me.user.id;
  const [row, setRow] = useState(null);
  const [segs, setSegs] = useState([]);
  const load = useCallback(async (types) => {
    const needMe = !types || [...types].some((t) => ME_EVENTS.test(t));
    const [r, f] = await Promise.all([api(`/tasks?date=${me.today}&person=${uid}`), api(`/floor?date=${me.today}&team=${me.user.team}`), needMe && refreshMe()]);
    if (!r.ok) return;
    setTasks(r.data.items);
    if (f.ok) setRow(f.data.rows.find((x) => x.person.id === uid) || null);
    const withTime = r.data.items.filter((t) => t.loggedMs > 0 || t.runningSince);
    const all = await Promise.all(withTime.map((t) => api(`/tasks/${t.id}/segments`).then((x) => (x.ok ? (x.data.items || []).map((g) => ({ ...g, client: t.client, title: t.title })) : []))));
    setSegs(all.flat().filter((g) => g.date === me.today).map((g, i) => ({ id: g.id || i, start: g.start, end: g.end || null, client: g.client, title: g.title })).sort((a, b) => b.start - a.start));
  }, [me.today, uid, me.user.team, refreshMe]);
  useEffect(() => {
    load(FIRST);
    api("/clients").then((r) => {
      if (!r.ok) return;
      setClients(r.data.items.filter((c) => c.active));
      setNames(Object.fromEntries(r.data.items.map((c) => [c.code, c.name])));
    });
  }, [load]);
  useEffect(() => syncClock(me.serverNow), [me.serverNow]);
  useLive(load, MYDAY_EVENTS);
  const run = async (p) => { await act(p); load(); };
  const keys = useRef({});
  useEffect(() => {
    const onKey = (e) => {
      if (e.repeat || e.metaKey || e.ctrlKey || e.altKey) return;
      const el = document.activeElement;
      if (el?.closest?.("form, input, textarea, select, [contenteditable], [role=dialog], [role=menu], [role=listbox], [role=alertdialog]")
        || document.querySelector("[data-no-shortcuts]") || document.getElementById("root")?.hasAttribute("inert")) return;
      const k = e.key.toLowerCase(), x = keys.current;
      if (x.sheet || ((k === "p" || k === "b" || k === "u") && x.off)) return;
      if (k === "p") { if (x.running) x.run(post("/timer/pause", { taskId: x.running.id, reason: "switch" })); else if (x.next) x.run(post("/timer/play", { taskId: x.next.id })); else return; }
      else if (k === "c") { if (!x.running) return; window.dispatchEvent(new Event("ck-key-complete")); }
      else if (k === "b") x.run(post(x.brk ? "/timer/break/end" : "/timer/break/start"));
      else if (k === "u") setSheet({ kind: "unplanned" });
      else return;
      e.preventDefault();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, []);
  if (!tasks) return <div className="loading" data-testid="myday-loading">Loading your day…</div>;
  const running = tasks.find((t) => t.runningSince);
  const brk = me.timer?.openBreak;
  const open = tasks.filter((t) => t.state !== "done" && !t.runningSince);
  const done = tasks.filter((t) => t.state === "done");
  const off = !!me.away;
  keys.current = { running, brk, next: open.find((t) => !PARKED.includes(t.state) && t.kind !== "standby"), off, sheet, run };
  const listed = [...tasks.filter((t) => t.state !== "done")];
  const [d0, d1, d2] = dayLabel(me.today).toUpperCase().split(" ");
  const left = Math.max(0, istMinutes(me.shift.end) - istMinutes(istHM24(Date.now())));
  return (
    <div className="myday" data-team={me.user.team} data-testid="myday-page">
      <div className="myday-grid">
        <div className="md-left">
          <div className="md-head">
            <h1 className="stackh"><span className="hl-ink">{d0}</span> <span className="hl-mari">{d1}</span> <span className="hl-g">{d2}</span></h1>
            <div className="md-who"><b>{me.user.name}</b><div>{me.user.designation}</div>
              <div>Duty {hm12(me.shift.start)} – {hm12(me.shift.end)} IST{left > 0 ? <>, <b className="num">{dur(left * 60000)}</b> left</> : ""}</div></div>
          </div>
          <Stats tasks={tasks} row={row} shift={me.shift} />
          {off && (
            <div className="banner" data-testid="leave-banner">
              <b>{me.away.roster ? "Not on today's roster." : "On leave today, so timers are off."}</b>
              {!me.away.roster && <button className="btn-ghost sm" onClick={() => run(post("/leaves/end", { person: uid, date: me.today }))}
                data-testid="back-at-work-button">Back at work</button>}
            </div>
          )}
          <NowPlaying card={running} names={names} brk={brk} next={open[0]}
            onPause={(c, r) => run(r === "break" ? post("/timer/break/start") : post("/timer/pause", { taskId: c.id, reason: r }))}
            onDone={(c, o) => run(post("/timer/complete", { taskId: c.id, outcome: o }))}
            onEndBreak={() => run(post("/timer/break/end"))} />
          {!off && <div className="md-actions">
            {!brk && <button className="btn-solid big" onClick={() => run(post("/timer/break/start"))} data-testid="break-button">Break <small className="num">{dur(row?.breakLeftMs ?? 0)} left</small><Kbd k="B" dark /></button>}
            <button className="btn-ghost big" onClick={() => setSheet({ kind: "unplanned" })} data-testid="log-unplanned-button">+ Log unplanned<Kbd k="U" /></button>
          </div>}
          <Ribbon tasks={tasks} segs={segs} me={me} />
          <Hours tasks={tasks} names={names} segs={segs} />
        </div>
        <section className="md-right">
          <div className="md-lh"><h2>Your list <span className="num i2">{listed.length} open</span></h2>
            <span className="fnote">Order set at planning. Planners and your lead rearrange, you play.</span></div>
          {listed.length === 0 && <p className="empty" data-testid="myday-empty">Nothing on your list yet.</p>}
          <ul className="clist" data-testid="myday-list">
            {listed.map((t, i) => <CardRow key={t.id} t={t} n={i + 1} names={names} off={off} onPlay={(p, b) => run(post(p, b))}
              onAddTime={(x) => setSheet({ kind: "time", task: x })} />)}
          </ul>
          <div className="md-lh mt"><span className="lbl">Done pile</span><span className="num hint0">{done.length} done, {dur(done.reduce((s, t) => s + t.loggedMs, 0))}</span></div>
          {done.length === 0 && <p className="empty">Nothing done yet. Complete a card and it lands here.</p>}
          <ul className="clist done" data-testid="myday-done">
            {done.map((t) => <CardRow key={t.id} t={t} names={names} off onPlay={() => {}} onAddTime={() => {}} />)}
          </ul>
        </section>
      </div>
      {sheet?.kind === "unplanned" && <UnplannedSheet clients={clients} onClose={() => setSheet(null)} onDone={() => { setSheet(null); load(); }} />}
      {sheet?.kind === "time" && <AddTimeSheet task={sheet.task} onClose={() => setSheet(null)} onDone={() => { setSheet(null); load(); }} />}
      {(running || brk) && <div className="dock-space" />}
      <Dock card={running} names={names} brk={brk} onBreak={() => run(post("/timer/break/start"))}
        onPause={(c, r) => run(post("/timer/pause", { taskId: c.id, reason: r }))}
        onDone={(c, o) => run(post("/timer/complete", { taskId: c.id, outcome: o }))} onEndBreak={() => run(post("/timer/break/end"))} />
    </div>
  );
}
